import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Gift, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ImportExportLinks } from '@/features/imports-exports';
import { useStore } from '@/shared/store/store-context';

import { useGiftCardColumns } from '../components/gift-card-columns';
import { giftCardFilters } from '../components/gift-card-filters';
import { IssueGiftCardDialog } from '../components/issue-gift-card-dialog';
import { useGiftCards } from '../hooks/use-gift-cards';
import { withLast4 } from '../schemas/gift-card-list-search';

const ROUTE_ID = '/_authed/_active/s/$storeId/gift-cards/' as const;
const ROUTE_PATH = '/s/$storeId/gift-cards/' as const;

function GiftCards() {
  const { t } = useTranslation('giftCards');
  const store = useStore();
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const [issuing, setIssuing] = useState(false);

  const filters = useMemo(
    () => giftCardFilters({ t, queryClient, storeId: store.id, currency: store.defaultCurrency }),
    [queryClient, store.defaultCurrency, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useGiftCards(store.id, withLast4(list.params));
  const columns = useGiftCardColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'gift-cards',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  const issueButton = (tour?: boolean) => (
    <Can permission="gift_cards.write">
      <Button
        onClick={() => setIssuing(true)}
        data-tour={tour ? 'gift-cards.list.issue' : undefined}
      >
        <Plus aria-hidden />
        {t('issue.action')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name, currency: store.defaultCurrency })}
        actions={
          <>
            <ImportExportLinks storeId={store.id} kind="gift_cards_csv" />
            {issueButton(true)}
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={Gift}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="gift-cards.list.empty"
          tourLink
          action={issueButton()}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/gift-cards/$giftCardId',
              params: { storeId: store.id, giftCardId: row.id },
            })
          }
          getRowLabel={(row) => t('masked', { last4: row.last4 })}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'gift-cards.list.table', search: 'gift-cards.list.search' }}
        />
      )}
      <IssueGiftCardDialog
        storeId={store.id}
        currency={store.defaultCurrency}
        open={issuing}
        onOpenChange={setIssuing}
      />
    </PageContent>
  );
}

export function GiftCardsPage() {
  return (
    <FeatureErrorBoundary name="giftCards.list">
      <GiftCards />
    </FeatureErrorBoundary>
  );
}
